/**
 * Retrieves the user type preference from a user object.
 * @param {Object|null} user - The user object containing id and attributes.
 * @returns {string|null} The user's type preference or null if not found or invalid.
 * @throws {TypeError} If the user parameter is not an object or null.
 */
export const getUserTypePreference = user => {
  // Validate input
  if (user !== null && typeof user !== 'object') {
    throw new TypeError('User parameter must be an object or null');
  }

  // Safely access nested properties using optional chaining
  return user?.id?.uuid ? user.attributes?.profile?.publicData?.userTypePreference ?? null : null;
};

export const getUserBio = user => {
  // Validate input
  if (user !== null && typeof user !== 'object') {
    throw new TypeError('User parameter must be an object or null');
  }

  // Safely access nested properties using optional chaining
  return user?.id?.uuid ? user.attributes?.profile?.bio ?? null : null;
};
export const getUsername = user => {
  // Validate input
  if (user !== null && typeof user !== 'object') {
    throw new TypeError('User parameter must be an object or null');
  }

  // Safely access nested properties using optional chaining
  return user?.id?.uuid ? user.attributes?.profile?.publicData?.username ?? null : null;
};

export const getLastLogin = user => {
  // Validate input
  if (user !== null && typeof user !== 'object') {
    throw new TypeError('User parameter must be an object or null');
  }

  // Safely access nested properties using optional chaining
  return user?.id?.uuid ? user.attributes?.profile?.publicData?.lastLogin ?? null : null;
};
export const getCoverImage = user => {
  // Validate input
  if (user !== null && typeof user !== 'object') {
    throw new TypeError('User parameter must be an object or null');
  }

  // Safely access nested properties using optional chaining
  return user?.id?.uuid ? user.attributes?.profile?.publicData?.coverImage ?? null : null;
};

export const getProfileImageUrl = (user, variant = 'square-small') => {
  // Validate input
  if (user !== null && typeof user !== 'object') {
    throw new TypeError('User parameter must be an object or null');
  }

  // Safely access nested properties using optional chaining
  return user?.id?.uuid ? user.profileImage?.attributes?.variants[variant]?.url ?? null : null;
};

export function retrieveUserWishlist(user) {
  if (!user?.id) return null;

  return user.attributes?.profile?.publicData?.wishlist || [];
}

export const getLikes = listing => {
  if (!listing?.id) return null;
  return listing?.attributes?.publicData?.likes || [];
};

export function getUserCartDetails(user) {
  // Consolidated null/undefined checks for robustness:
  if (!user || !user.id) {
    // Handle missing user or user ID gracefully
    return null;
  }

  // Combine optional chaining with default value for concise and safe access:
  const protectedData = user.attributes?.profile?.protectedData || {};
  return protectedData.cartItems ?? [];
}
export function getUserShops(user) {
  // Consolidated null/undefined checks for robustness:
  if (!user || !user.id) {
    // Handle missing user or user ID gracefully
    return null;
  }

  // Combine optional chaining with default value for concise and safe access:
  const privateData = user.attributes?.profile?.privateData || {};
  return privateData.shops ?? [];
}
export function getShopifyData(user) {
  // Consolidated null/undefined checks for robustness:
  if (!user || !user.id) {
    // Handle missing user or user ID gracefully
    return null;
  }

  // Combine optional chaining with default value for concise and safe access:
  const privateData = user.attributes?.profile?.privateData || {};
  return privateData.shopifyData ?? [];
}

/**
 * Extracts the shop from a listing's publicData if the listing is valid.
 * @param {Object} listing - The listing object.
 * @returns {string|null} The shop value or null if invalid/missing.
 */
export const getListingShop = listing => {
  if (!listing?.id?.uuid) {
    return null;
  }
  return listing.attributes?.publicData?.shop ?? null;
};

/**
 * Extracts the productId from a listing's publicData if the listing is valid.
 * @param {Object} listing - The listing object.
 * @returns {string|null} The productId value or null if invalid/missing.
 */
export const getProductId = listing => {
  if (!listing?.id?.uuid) {
    return null;
  }
  return listing.attributes?.publicData?.productId ?? null;
};

export const getUserEmail = user => (user?.id?.uuid ? user.attributes?.email : null);

export const getUserPhoneNumber = user =>
  user?.id?.uuid ? user.attributes?.profile?.protectedData?.phoneNumber : null;

export const getDisplayName = user =>
  user?.id?.uuid ? user.attributes?.profile?.displayName : 'N/A';

export const getReturnParams = txn =>
  txn?.id?.uuid ? txn.attributes?.metadata?.returnParams : null;

export const getUserFullName = user => {
  if (!user?.id) return null;
  const { firstName, lastName, displayName } = user.attributes?.profile ?? {};
  return firstName && lastName ? `${firstName} ${lastName}` : displayName;
};

export const getShippingDetails = user =>
  user?.attributes?.profile?.protectedData?.shippingAddress ?? null;

export const getUserCountry = user => user?.attributes?.profile?.publicData?.country ?? null;

export const getShippingPreference = user => {
  const { protectedData, publicData } = user?.attributes?.profile ?? {};

  return protectedData?.shippingPreference ?? publicData?.shippingPreference ?? null;
};

export const getShippingDimensions = user => {
  const shippingDimensions = user?.attributes?.profile?.publicData?.shippingDimensions;

  return (
    shippingDimensions ?? {
      height: '1',
      length: '8.5',
      width: '11',
      weight: '1.2',
      unit: 'lb',
      distanceUnit: 'in',
    }
  );
};

export const getAddressObjectId = user =>
  user?.id ? user.attributes?.profile?.publicData?.addressObjectId : null;

export const getPayInTotal = tx => (tx?.id ? tx.attributes?.payinTotal?.amount : null);

export const getCarrierParams = tx => (tx?.id ? tx.attributes?.protectedData?.carrierParams : null);

export const getProductWeight = listing => {
  return listing?.attributes?.publicData?.weight ?? 0;
};

export const getMassUnit = listing => (listing?.id ? listing.attributes?.publicData?.unit : null);

export const getPayInTotalCurrency = tx => tx?.attributes?.payinTotal?.currency ?? 'GBP';

export const getTrackingInfo = tx => (tx?.id ? tx.attributes?.metadata?.trackingInfo : null);

export const getReturnTrackingInfo = tx =>
  tx?.id ? tx.attributes?.metadata?.returnTrackingInfo : null;

export const getTransactionCart = tx => {
  if (!tx?.id) return [];

  const rawCart = tx?.attributes?.protectedData?.cartItems;

  if (!rawCart) return [];

  try {
    const parsed = JSON.parse(rawCart);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

export const getDestinationAddress = tx =>
  tx?.id ? tx.attributes?.metadata?.destinationAddress : null;

export const getOfferParams = transaction => {
  // Must have a valid UUID to consider this a real transaction
  if (!transaction?.id?.uuid) {
    return null;
  }

  // Drill into protectedData and return offerParams if it exists
  return transaction.attributes?.protectedData?.offerParams ?? null;
};
export const getUserOfferAvailability = user => {
  if (!user?.id?.uuid) {
    return null;
  }
  return user?.attributes?.profile?.publicData?.isAvailableForOffers ?? false;
};

/**
 * Safely extracts the listing relationship from a transaction response.
 * Returns `null` if not present or invalid.
 */
export const listingRelationship = txResponse => {
  return txResponse?.data?.data?.relationships?.listing?.data ?? null;
};

export const extractImageUrl = data => {
  const variants = data?.data?.data?.attributes?.variants;
  return variants?.default?.url ?? null;
};

export const getLastActive = user => {
  if (!user?.id?.uuid) {
    return null;
  }
  return user?.attributes?.profile?.publicData?.lastActive ?? null;
};

export const getPaypalMerchantId = user => {
  if (!user?.id?.uuid) {
    return null;
  }
  return user?.attributes?.profile?.publicData?.merchantId ?? null;
};

export const getWalletAvailability = user => {
  if (!user?.id?.uuid) {
    return false;
  }
  return user?.attributes?.profile?.publicData?.walletPaymentsEnabled ?? false;
};

export const getWalletDetails = user => {
  if (!user?.id?.uuid) {
    return false;
  }
  return user?.attributes?.profile?.protectedData?.walletDetails ?? {};
};

export const getClientSecret = setupIntent => {
  return setupIntent && setupIntent.attributes ? setupIntent.attributes.clientSecret : null;
};

export const getPaymentParams = (currentUser, formValues) => {
  const { line1, line2, postal_code, state, city, country } = formValues;
  const addressMaybe =
    line1 && postal_code
      ? {
          address: {
            city: city,
            country: country,
            line1: line1,
            line2: line2,
            postal_code: postal_code,
            state: state,
          },
        }
      : {};
  const userFullName = getUserFullName(currentUser);
  const email = getUserEmail(currentUser);
  const billingDetails = {
    name: userFullName,
    email,
    ...addressMaybe,
  };

  const paymentParams = {
    payment_method_data: {
      billing_details: billingDetails,
    },
  };

  return paymentParams;
};
